<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { ArrowLeft } from 'lucide-vue-next'
import { CkAlert, CkStatus } from '@ck/ui'
import { checklistsApi, workOrdersApi } from '@ck/api-client'
import { formatDateTimeBr } from '@ck/domain'

const router = useRouter()

function isToday(iso: string) {
  const d = new Date(iso)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
}

const historyQuery = useQuery({
  queryKey: ['historico-do-dia'],
  queryFn: async () => {
    const { items } = await workOrdersApi.list()
    const perOrder = await Promise.all(items.map(async (workOrder) => {
      const stages = await checklistsApi.get(workOrder.id)
      return stages.flatMap((stage) => stage.items
        .filter((item) => item.capturedAt && isToday(item.capturedAt))
        .map((item) => ({ ...item, stageTitle: stage.title, vehicle: workOrder.vehicle, workOrderNumber: workOrder.number })))
    }))
    return perOrder.flat().sort((a, b) => (b.capturedAt ?? '').localeCompare(a.capturedAt ?? ''))
  }
})

</script>

<template>
  <div class="app">
    <header>
      <button type="button" class="back" aria-label="Voltar" @click="router.push({ name: 'perfil' })"><ArrowLeft :size="20"/></button>
      <h1>Histórico do dia</h1>
    </header>

    <CkAlert v-if="historyQuery.isError.value" tone="error">Não foi possível carregar o histórico.</CkAlert>
    <p v-else-if="historyQuery.isPending.value" class="status-message">Carregando…</p>
    <p v-else-if="historyQuery.data.value?.length === 0" class="status-message">Nenhum item avaliado hoje ainda.</p>
    <template v-else>
      <article v-for="entry in historyQuery.data.value" :key="entry.id" class="row">
        <span class="time ck-code">{{ formatDateTimeBr(entry.capturedAt) }}</span>
        <div class="body">
          <strong>{{ entry.title }}</strong>
          <span class="meta">{{ entry.vehicle.brand }} {{ entry.vehicle.model }} · {{ entry.stageTitle }}</span>
        </div>
        <CkStatus :status="entry.status" compact/>
      </article>
    </template>
  </div>
</template>

<style scoped>
.app{max-width:620px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px 40px;background:var(--ck-cream-100)}
header{display:flex;align-items:center;gap:12px;margin-bottom:18px}
.back{display:flex;align-items:center;justify-content:center;width:38px;height:38px;flex-shrink:0;border:1px solid var(--ck-cream-300);border-radius:11px;background:var(--ck-cream-50);color:var(--ck-maroon-700);cursor:pointer}
header h1{margin:0;font-size:24px}
.status-message{padding:20px 0;color:var(--ck-text-muted);font-size:13px}
.row{display:flex;align-items:center;gap:12px;padding:12px 14px;margin-bottom:8px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50)}
.time{flex-shrink:0;color:var(--ck-text-faint);font-size:11px}
.body{flex:1;min-width:0}
.body strong{display:block;font-size:14px}
.meta{display:block;color:var(--ck-text-muted);font-size:12px;margin-top:2px}
</style>
